/**
 * Endgame detection - checkmate, stalemate, and draw conditions
 */

import type { GameState } from './types';
import { Color, PieceType } from './types';
import { generateLegalMoves, isInCheck, findKingSquare } from './moves';
import { getPositionHash } from './board';

/**
 * Check if the current position is checkmate
 * @param state The game state to check
 * @returns True if the current player is in checkmate
 */
export function isCheckmate(state: GameState): boolean {
  // Checkmate requires:
  // 1. King is in check
  // 2. No legal moves available
  
  const kingSquare = findKingSquare(state, state.activeColor);
  if (kingSquare === null) {
    return false; // No king found (shouldn't happen in valid game)
  }
  
  const inCheck = isInCheck(state, state.activeColor);
  if (!inCheck) {
    return false; // Not in check, so not checkmate
  }
  
  const legalMoves = generateLegalMoves(state);
  return legalMoves.length === 0;
}

/**
 * Check if the current position is stalemate
 * @param state The game state to check
 * @returns True if the current player is in stalemate
 */
export function isStalemate(state: GameState): boolean {
  // Stalemate requires:
  // 1. King is NOT in check
  // 2. No legal moves available
  
  const kingSquare = findKingSquare(state, state.activeColor);
  if (kingSquare === null) {
    return false; // No king found (shouldn't happen in valid game)
  }
  
  const inCheck = isInCheck(state, state.activeColor);
  if (inCheck) {
    return false; // In check, so not stalemate
  }
  
  const legalMoves = generateLegalMoves(state);
  return legalMoves.length === 0;
}

/**
 * Check if the position has insufficient material to checkmate
 * @param state The game state to check
 * @returns True if neither side can possibly checkmate
 */
export function isInsufficientMaterial(state: GameState): boolean {
  // Count pieces for each side
  const whitePieces: PieceType[] = [];
  const blackPieces: PieceType[] = [];
  
  for (let square = 0; square < 64; square++) {
    const piece = state.board[square];
    if (piece) {
      if (piece.type !== PieceType.King) {
        if (piece.color === Color.White) {
          whitePieces.push(piece.type);
        } else {
          blackPieces.push(piece.type);
        }
      }
    }
  }
  
  // King vs King
  if (whitePieces.length === 0 && blackPieces.length === 0) {
    return true;
  }
  
  // King + Knight vs King
  if (whitePieces.length === 1 && blackPieces.length === 0) {
    if (whitePieces[0] === PieceType.Knight) {
      return true;
    }
  }
  if (blackPieces.length === 1 && whitePieces.length === 0) {
    if (blackPieces[0] === PieceType.Knight) {
      return true;
    }
  }
  
  // King + Bishop vs King
  if (whitePieces.length === 1 && blackPieces.length === 0) {
    if (whitePieces[0] === PieceType.Bishop) {
      return true;
    }
  }
  if (blackPieces.length === 1 && whitePieces.length === 0) {
    if (blackPieces[0] === PieceType.Bishop) {
      return true;
    }
  }
  
  // King + Bishop vs King + Bishop (same color squares)
  if (whitePieces.length === 1 && blackPieces.length === 1) {
    if (whitePieces[0] === PieceType.Bishop && blackPieces[0] === PieceType.Bishop) {
      // Find the bishops to check their square colors
      let whiteBishopSquare = -1;
      let blackBishopSquare = -1;
      
      for (let square = 0; square < 64; square++) {
        const piece = state.board[square];
        if (piece && piece.type === PieceType.Bishop) {
          if (piece.color === Color.White) {
            whiteBishopSquare = square;
          } else {
            blackBishopSquare = square;
          }
        }
      }
      
      if (whiteBishopSquare !== -1 && blackBishopSquare !== -1) {
        // Check if bishops are on same color squares
        // Square color is determined by (rank + file) % 2
        const whiteSquareColor = (Math.floor(whiteBishopSquare / 8) + (whiteBishopSquare % 8)) % 2;
        const blackSquareColor = (Math.floor(blackBishopSquare / 8) + (blackBishopSquare % 8)) % 2;
        
        if (whiteSquareColor === blackSquareColor) {
          return true;
        }
      }
    }
  }
  
  return false;
}

/**
 * Check if the 50-move rule applies (draw by 50 moves without pawn move or capture)
 * @param state The game state to check
 * @returns True if 50 moves have been made without a pawn move or capture
 */
export function isFiftyMoveRule(state: GameState): boolean {
  // The halfmove clock counts plies (half-moves)
  // 50 full moves = 100 half-moves
  return state.halfmoveClock >= 100;
}

/**
 * Check if the position has occurred three times (three-fold repetition)
 * @param state The game state to check
 * @returns True if the current position has occurred at least 3 times
 */
export function isThreefoldRepetition(state: GameState): boolean {
  const currentHash = getPositionHash(state);
  
  // Count how many times the current position appears in the history
  let count = 0;
  for (const hash of state.positionHistory) {
    if (hash === currentHash) {
      count++;
    }
  }
  
  return count >= 3;
}

/**
 * Check if the game is a draw by any rule
 * @param state The game state to check
 * @returns True if the game is a draw
 */
export function isDraw(state: GameState): boolean {
  return isStalemate(state) || isInsufficientMaterial(state) || isFiftyMoveRule(state) || isThreefoldRepetition(state);
}

/**
 * Check if the game is over (checkmate or draw)
 * @param state The game state to check
 * @returns True if the game is over
 */
export function isGameOver(state: GameState): boolean {
  return isCheckmate(state) || isDraw(state);
}

/**
 * Get the result of the game
 * @param state The game state to check
 * @returns 'white' if white won, 'black' if black won, 'draw' if draw, or null if game is ongoing
 */
export function getGameResult(state: GameState): 'white' | 'black' | 'draw' | null {
  if (isCheckmate(state)) {
    // The active color is checkmated, so the other color wins
    return state.activeColor === Color.White ? 'black' : 'white';
  }
  
  if (isDraw(state)) {
    return 'draw';
  }
  
  return null;
}
